import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  enrollmentApi,
  coursesApi,
  mentorshipApi,
  paymentApi,
  showError,
  showSuccess,
} from "../lib";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Home,
  BookOpen,
  Video,
  Award,
  Calendar,
  Clock,
  ArrowRight,
  Sparkles,
} from "lucide-react";

// Helper to extract courseId from externalId (format: LEARNHUB_ENR_{userId}_{courseId}_{timestamp})
const extractCourseIdFromExternalId = (externalId: string): string | null => {
  const match = externalId?.match(/^LEARNHUB_ENR_[^_]+_(.+?)_\d+$/);
  return match ? match[1] : null;
};

// Helper to extract sessionId from externalId (format: LEARNHUB_MENTOR_{userId}_{sessionId}_{timestamp})
const extractSessionIdFromExternalId = (externalId: string): string | null => {
  const match = externalId?.match(/^LEARNHUB_MENTOR_[^_]+_(.+?)_\d+$/);
  return match ? match[1] : null;
};

type PaymentType = "course" | "session" | null;

const PaymentSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "failed">(
    "loading",
  );
  const [paymentType, setPaymentType] = useState<PaymentType>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [course, setCourse] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const searchParams = new URLSearchParams(location.search);
      const transactionId =
        searchParams.get("transactionId") || searchParams.get("transId");
      const externalId = searchParams.get("externalId");

      if (!transactionId) {
        setStatus("failed");
        showError("Missing transaction ID");
        return;
      }

      try {
        let attempts = 0;
        const maxAttempts = 20;
        const pollInterval = 2000;

        const isMentorshipPayment = externalId?.startsWith("LEARNHUB_MENTOR_");

        // Poll for course payment status
        while (attempts < maxAttempts) {
          try {
            const paymentRes =
              await paymentApi.checkPaymentStatus(transactionId);

            if (paymentRes.success && paymentRes.data?.status === "completed") {
              setPaymentType("course");
              setPaymentDetails(paymentRes.data);
              setStatus("success");

              // Get courseId
              let courseIdToUse: string | null = null;

              if (paymentRes.data.course) {
                courseIdToUse = paymentRes.data.course;
              } else if (externalId) {
                courseIdToUse = extractCourseIdFromExternalId(externalId);
              }

              if (!courseIdToUse) {
                const courseIdFromStorage =
                  sessionStorage.getItem("pendingEnrollment");
                if (courseIdFromStorage) {
                  try {
                    const pendingData = JSON.parse(courseIdFromStorage);
                    courseIdToUse = pendingData.courseId;
                  } catch {
                    // Ignore parse errors
                  }
                }
              }

              if (courseIdToUse) {
                setTargetId(courseIdToUse);
                const courseRes = await coursesApi.getCourseById(courseIdToUse);
                if (courseRes.success) {
                  setCourse(courseRes.data);
                }

                // ✅ FIXED: Use the correct enrollment method - enrollAfterPayment
                // First, try to enroll after payment
                try {
                  // Option 1: Use payment success endpoint
                  await enrollmentApi.enrollAfterPayment(courseIdToUse, {
                    paymentId: paymentRes.data.paymentId || transactionId,
                    paymentMethod: "fapshi",
                    transactionId: transactionId,
                  });
                  console.log("✅ Enrollment completed via payment success");
                  showSuccess("Successfully enrolled in the course!");
                } catch (enrollError: any) {
                  console.error(
                    "Enrollment via payment success failed:",
                    enrollError.message,
                  );

                  // Option 2: Try direct enrollment (check if payment was already processed)
                  try {
                    const checkEnrollment =
                      await enrollmentApi.checkEnrollmentStatus(courseIdToUse);
                    if (!checkEnrollment.isEnrolled) {
                      await enrollmentApi.enrollInCourse(courseIdToUse);
                      console.log(
                        "✅ Enrollment completed via direct enrollment",
                      );
                      showSuccess("Successfully enrolled in the course!");
                    } else {
                      console.log("✅ User already enrolled");
                      showSuccess("You are already enrolled in this course!");
                    }
                  } catch (directError: any) {
                    console.error(
                      "Direct enrollment also failed:",
                      directError.message,
                    );
                    // Don't throw - just log, user can still access course
                  }
                }
              }
              return;
            }
          } catch (err) {
            console.log("Payment check attempt", attempts + 1, "failed:", err);
          }

          attempts++;
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }

        // Check for mentorship payment
        if (isMentorshipPayment) {
          attempts = 0;
          while (attempts < maxAttempts) {
            try {
              const sessionRes =
                await paymentApi.checkMentorshipPaymentStatus(transactionId);
              if (
                sessionRes.success &&
                sessionRes.data?.status === "completed"
              ) {
                setPaymentType("session");
                setPaymentDetails(sessionRes.data);
                setStatus("success");

                if (sessionRes.data.session) {
                  setTargetId(sessionRes.data.session);
                  const sessionDetails = await mentorshipApi.getSessionDetails(
                    sessionRes.data.session,
                  );
                  if (sessionDetails.success) {
                    setSession(sessionDetails.data);
                  }
                }
                return;
              }
            } catch (err) {
              console.log(
                "Mentorship check attempt",
                attempts + 1,
                "failed:",
                err,
              );
            }
            attempts++;
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
          }
        }

        setStatus("failed");
        showError("Payment verification timed out. Please contact support.");
      } catch (err: unknown) {
        console.error("Payment verification error:", err);
        setStatus("failed");
        showError(
          err instanceof Error ? err.message : "Failed to verify payment",
        );
      }
    };

    verifyPayment();
  }, [location.search]);

  useEffect(() => {
    if (status === "success" && targetId && paymentType) {
      const timer = setTimeout(() => {
        if (paymentType === "session") {
          navigate("/dashboard/student/mentorship-sessions", { replace: true });
        } else if (paymentType === "course") {
          navigate("/dashboard/student/courses", { replace: true });
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, targetId, paymentType, navigate]);

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  const sessionDateTime = session?.scheduledAt
    ? formatDateTime(session.scheduledAt)
    : null;

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center px-4">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-orange-400/20 animate-ping"></div>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-orange-500 animate-spin relative z-10" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Verifying Payment...
          </h2>
          <p className="text-sm text-gray-600">
            Please wait while we confirm your payment status.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            This may take a few seconds
          </p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 px-4 py-8">
        <div className="w-full max-w-md p-6 text-center bg-white rounded-2xl shadow-xl border border-gray-100">
          <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">
            Payment Failed
          </h2>
          <p className="mb-6 text-sm text-gray-600">
            Your payment could not be processed. Please try again or contact
            support.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/courses")}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
            >
              Browse Courses
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 px-4 py-8">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* Success Header */}
        <div className="relative bg-gradient-to-r from-emerald-600 to-green-500 py-8">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 30% 60%, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 70% 30%, rgba(255,255,255,0.3) 1px, transparent 1px)",
              backgroundSize: "20px 20px, 30px 30px",
            }}
          />
          <div className="relative text-center">
            <div className="w-20 h-20 mx-auto mb-3 bg-white/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">
              Payment Successful!
            </h1>
            <p className="text-emerald-100 text-sm mt-1">
              {paymentType === "session"
                ? "Your mentorship session is confirmed"
                : "Your enrollment is confirmed"}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Course Details */}
          {paymentType === "course" && course && (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-orange-500" />
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Course Details
                </h3>
              </div>
              <div className="flex items-start gap-3">
                {course.thumbnail && (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1">
                  <h4 className="text-sm font-bold text-gray-900">
                    {course.title}
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                    {course.shortDescription ||
                      course.description?.substring(0, 100)}
                  </p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                      {course.difficulty || "Beginner"}
                    </span>
                    <span className="text-xs text-gray-500">
                      {course.totalLessons || 0} lessons
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Session Details */}
          {paymentType === "session" && session && (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Video className="w-4 h-4 text-orange-500" />
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Session Details
                </h3>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900">
                  {session.topic || "Mentorship Session"}
                </p>
                {sessionDateTime && (
                  <>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{sessionDateTime.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Clock className="w-3.5 h-3.5" />
                      <span>
                        {sessionDateTime.time} • {session.duration || 60}{" "}
                        minutes
                      </span>
                    </div>
                  </>
                )}
                {session.meetingLink && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <a
                      href={session.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-700"
                    >
                      <Video className="w-3 h-3" />
                      Join Meeting Link
                      <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Info */}
          {paymentDetails && (
            <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-4 h-4 text-emerald-600" />
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Payment Information
                </h3>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Amount Paid</span>
                <span className="text-lg font-bold text-gray-900">
                  {paymentDetails.amount
                    ? new Intl.NumberFormat("fr-CM", {
                        style: "currency",
                        currency: "XAF",
                        maximumFractionDigits: 0,
                      }).format(paymentDetails.amount)
                    : "Processing"}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-500">Transaction ID</span>
                <span className="text-xs font-mono text-gray-600">
                  {paymentDetails.transactionId?.slice(-12) || "Pending"}
                </span>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-orange-500" />
              <h3 className="text-sm font-semibold text-gray-900">
                What's Next?
              </h3>
            </div>
            <ul className="space-y-2 text-xs text-gray-600">
              {paymentType === "session" ? (
                <>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                    <span>Your session is confirmed with the mentor</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                    <span>
                      A meeting link will be available before the session
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                    <span>You can view all your sessions in the dashboard</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                    <span>The course has been added to your dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                    <span>Start learning immediately from your dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                    <span>Track your progress and earn certificates</span>
                  </li>
                </>
              )}
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                <span>A confirmation email has been sent to your inbox</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            {paymentType === "session" ? (
              <>
                <button
                  onClick={() =>
                    navigate("/dashboard/student/mentorship-sessions")
                  }
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg transition-all duration-300"
                >
                  <Video className="w-4 h-4" />
                  View My Sessions
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate("/mentorship")}
                  className="w-full px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                >
                  Browse More Mentors
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() =>
                    targetId && navigate(`/course/${targetId}/learn`)
                  }
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg transition-all duration-300"
                >
                  <BookOpen className="w-4 h-4" />
                  Start Learning Now
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate("/dashboard/student/courses")}
                  className="w-full px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                >
                  View My Courses
                </button>
              </>
            )}
          </div>

          {/* Auto Redirect Note */}
          <p className="mt-4 text-center text-[10px] text-gray-400">
            Redirecting to your{" "}
            {paymentType === "session" ? "sessions" : "courses"} in a few
            seconds...
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
